import api from '@/api'
import io from 'socket.io-client'

const state = {
  socket: null,
  connected: false,
  realTimeData: {},
  deviceStatus: {},
  sensorReadings: {},
  lastUpdate: null,
  loading: false,
  error: null,
  sensors: [],
  devices: [],
  readings: {}
}

const mutations = {
  SET_SOCKET(state, socket) {
    state.socket = socket
  },
  SET_CONNECTED(state, connected) {
    state.connected = connected
  },
  UPDATE_REAL_TIME_DATA(state, data) {
    state.realTimeData = { ...state.realTimeData, ...data }
    state.lastUpdate = new Date().toISOString()
  },
  UPDATE_DEVICE_STATUS(state, { deviceId, status }) {
    state.deviceStatus = { ...state.deviceStatus, [deviceId]: status }
  },
  UPDATE_SENSOR_READINGS(state, { sensorId, readings }) {
    state.sensorReadings = { ...state.sensorReadings, [sensorId]: readings }
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  CLEAR_ERROR(state) {
    state.error = null
  },
  SET_SENSORS(state, sensors) {
    state.sensors = sensors
  },
  SET_DEVICES(state, devices) {
    state.devices = devices
  },
  SET_READINGS(state, readings) {
    state.readings = { ...state.readings, ...readings }
  }
}

const actions = {
  async initialize({ commit, dispatch }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      // Initialize WebSocket connection
      await dispatch('initializeSocket')
      
      // Load initial data
      await dispatch('loadInitialData')
      
    } catch (error) {
      // Error initializing IoT module - handled silently
      commit('SET_ERROR', 'Error al inicializar conexiones IoT')
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async initializeSocket({ commit, rootGetters }) {
    return new Promise((resolve, reject) => {
      try {
        const token = rootGetters['auth/token']
        if (!token) {
          reject(new Error('No authentication token available'))
          return
        }

        const socket = io(process.env.VUE_APP_WEBSOCKET_URL || 'ws://localhost:3001', {
          auth: {
            token
          },
          transports: ['websocket', 'polling']
        })

        socket.on('connect', () => {
          // WebSocket connected - handled silently
          commit('SET_CONNECTED', true)
          resolve(socket)
        })

        socket.on('disconnect', () => {
          // WebSocket disconnected - handled silently
          commit('SET_CONNECTED', false)
        })

        socket.on('error', (error) => {
          // WebSocket error - handled silently
          commit('SET_ERROR', 'Error de conexión WebSocket')
          reject(error)
        })

        // Handle real-time data updates
        socket.on('sensor_data', (data) => {
          commit('UPDATE_REAL_TIME_DATA', data)
        })

        socket.on('device_status', (data) => {
          commit('UPDATE_DEVICE_STATUS', data)
        })

        socket.on('sensor_readings', (data) => {
          commit('UPDATE_SENSOR_READINGS', data)
        })

        socket.on('alert', (alert) => {
          // Dispatch to alerts module
          this.$store.dispatch('alerts/addAlert', alert)
        })

        commit('SET_SOCKET', socket)

      } catch (error) {
        // Error creating WebSocket connection - handled silently
        reject(error)
      }
    })
  },

  async loadInitialData({ commit }) {
    try {
      const [devices, sensors] = await Promise.all([
        api.get('/devices'),
        api.get('/sensors')
      ])

      // Initialize device status
      const deviceStatus = {}
      devices.data.forEach(device => {
        deviceStatus[device.id] = {
          online: device.online,
          lastSeen: device.lastSeen,
          battery: device.battery,
          signal: device.signal
        }
      })
      commit('UPDATE_DEVICE_STATUS', deviceStatus)

      // Initialize sensor readings
      const sensorReadings = {}
      sensors.data.forEach(sensor => {
        sensorReadings[sensor.id] = {
          temperature: sensor.lastReading && sensor.lastReading.temperature,
          humidity: sensor.lastReading && sensor.lastReading.humidity,
          timestamp: sensor.lastReading && sensor.lastReading.timestamp
        }
      })
      commit('UPDATE_SENSOR_READINGS', sensorReadings)

    } catch (error) {
      // Error loading initial IoT data - handled silently
      commit('SET_ERROR', 'Error al cargar datos iniciales')
    }
  },

  async sendCommand({ state }, { deviceId, command, params = {} }) {
    try {
      if (!state.connected) {
        throw new Error('No hay conexión WebSocket activa')
      }

      const response = await new Promise((resolve, reject) => {
        state.socket.emit('device_command', { deviceId, command, params }, (response) => {
          if (response.success) {
            resolve(response)
          } else {
            reject(new Error(response.error || 'Error al enviar comando'))
          }
        })

        // Timeout after 10 seconds
        setTimeout(() => {
          reject(new Error('Timeout al enviar comando'))
        }, 10000)
      })

      return response
    } catch (error) {
      // Error sending device command - handled silently
      throw error
    }
  },

  async getDeviceData({ commit }, deviceId) {
    try {
      commit('SET_LOADING', true)
      const response = await api.get(`/devices/${deviceId}/data`)
      return response.data
    } catch (error) {
      // Error getting device data - handled silently
      commit('SET_ERROR', 'Error al obtener datos del dispositivo')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async getSensorHistory({ commit }, { sensorId, startDate, endDate, interval = '1h' }) {
    try {
      commit('SET_LOADING', true)
      const response = await api.get(`/sensors/${sensorId}/history`, { 
        params: { startDate, endDate, interval } 
      })
      return response.data
    } catch (error) {
      // Error getting sensor history - handled silently
      commit('SET_ERROR', 'Error al obtener historial del sensor')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  disconnect({ commit, state }) {
    if (state.socket) {
      state.socket.disconnect()
      commit('SET_SOCKET', null)
      commit('SET_CONNECTED', false)
    }
  },

  async fetchSensors({ commit }) {
    try {
      commit('SET_LOADING', true)
      const response = await api.get('/sensors')
      commit('SET_SENSORS', response.data)
    } catch (error) {
      commit('SET_ERROR', 'Error al cargar sensores')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchDevices({ commit }) {
    try {
      commit('SET_LOADING', true)
      const response = await api.get('/devices')
      commit('SET_DEVICES', response.data)
    } catch (error) {
      commit('SET_ERROR', 'Error al cargar dispositivos')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchReadings({ commit }, sensorIds) {
    try {
      const response = await api.get('/readings', {
        params: { sensorIds: sensorIds.join(',') }
      })
      
      const sensorReadings = {}
      response.data.forEach(reading => {
        if (!sensorReadings[reading.sensorId]) {
          sensorReadings[reading.sensorId] = []
        }
        sensorReadings[reading.sensorId].push(reading)
      })
      
      commit('SET_READINGS', sensorReadings)
    } catch (error) {
      commit('SET_ERROR', 'Error al cargar lecturas')
      throw error
    }
  },

  async getSensorReadings({ commit, state }) {
    try {
      const sensors = state.sensors
      const sensorReadings = {}
      
      sensors.forEach(sensor => {
        sensorReadings[sensor.id] = {
          temperature: sensor.lastReading && sensor.lastReading.temperature,
          humidity: sensor.lastReading && sensor.lastReading.humidity,
          timestamp: sensor.lastReading && sensor.lastReading.timestamp
        }
      })
      
      commit('SET_READINGS', sensorReadings)
    } catch (error) {
      commit('SET_ERROR', 'Error al procesar lecturas de sensores')
      throw error
    }
  }
}

const getters = {
  isConnected: state => state.connected,
  realTimeData: state => state.realTimeData,
  deviceStatus: state => state.deviceStatus,
  sensorReadings: state => state.sensorReadings,
  lastUpdate: state => state.lastUpdate,
  isLoading: state => state.loading,
  error: state => state.error,
  
  // Computed getters
  onlineDevices: state => {
    return Object.values(state.deviceStatus).filter(status => status.online).length
  },
  
  totalDevices: state => {
    return Object.keys(state.deviceStatus).length
  },
  
  activeSensors: state => {
    return Object.keys(state.sensorReadings).length
  },
  
  getDeviceStatus: state => deviceId => {
    return state.deviceStatus[deviceId] || { online: false, lastSeen: null }
  },
  
  getSensorReadings: state => sensorId => {
    return state.sensorReadings[sensorId] || { temperature: null, humidity: null, timestamp: null }
  },
  
  getRealTimeData: state => deviceId => {
    return state.realTimeData[deviceId] || {}
  },

  sensors: state => state.sensors,
  devices: state => state.devices,
  readings: state => state.readings,
  
  activeSensors: state => state.sensors.filter(sensor => sensor.status === 'active'),
  offlineSensors: state => state.sensors.filter(sensor => sensor.status === 'offline'),
  
  getSensorReading: (state) => (sensorId) => {
    return state.readings[sensorId] || null
  },
  
  getDeviceSensors: (state) => (deviceId) => {
    return state.sensors.filter(sensor => sensor.deviceId === deviceId)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
} 


// WEBPACK FOOTER //
// ./src/store/modules/iot.js